import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GetPreviewEntitiesDocument } from "~/graphql/supergraphHooks";
import type { PreviewEntityInfo } from "../types";
import type { GetPreviewEntitiesQuery } from "~/graphql/supergraphTypes";

type UsePreviewEntitiesResult = {
  entities: PreviewEntityInfo[];
  loading: boolean;
};

// Largest arenas hold ~4k entities; 250 per page keeps them to ~16 requests.
const PAGE_SIZE = 250;

type EntityEdges = NonNullable<GetPreviewEntitiesQuery["investmentEntitiesByArena"]>["edges"];

const toPreviewEntities = (edges: EntityEdges): PreviewEntityInfo[] =>
  edges
    .map((edge) => ({
      id: edge?.node?.id ?? "",
      name: edge?.node?.name ?? "",
      type: edge?.node?.type?.name ?? "",
      status: edge?.node?.status ?? "",
    }))
    .filter((entity) => entity.id && entity.name);

/**
 * Loads every investment entity in the arena for the preview Entity dropdown.
 *
 * `investmentEntitiesByArena` is paginated, so a single request only returns the
 * first page (PAGE_SIZE entities, name-sorted). The dropdown filters client-side, so
 * it needs the full list: this follows `pageInfo.endCursor` until
 * `hasNextPage` is false and reports `loading` until the last page arrives.
 *
 * Pages are read cache-first: each page is cached under its own `after` cursor,
 * so reopening the dropdown in the same session reuses them instead of refetching.
 */
export const usePreviewEntities = (arenaId: string): UsePreviewEntitiesResult => {
  const client = useApolloClient();
  // Keyed by the arena it was loaded for, so switching arenas never shows the
  // previous arena's entities and reports loading from the very first render.
  const [loaded, setLoaded] = useState<{ arenaId: string; entities: PreviewEntityInfo[] }>({
    arenaId: "",
    entities: [],
  });

  useEffect(() => {
    if (!arenaId) return undefined;

    let cancelled = false;

    const loadAllPages = async () => {
      const allEntities: PreviewEntityInfo[] = [];
      let after: string | undefined;
      let hasNextPage = true;

      while (hasNextPage && !cancelled) {
        const { data } = await client.query<GetPreviewEntitiesQuery>({
          query: GetPreviewEntitiesDocument,
          variables: { arenaId, first: PAGE_SIZE, after },
          fetchPolicy: "cache-first",
        });
        const connection = data?.investmentEntitiesByArena;
        allEntities.push(...toPreviewEntities(connection?.edges ?? []));
        hasNextPage = Boolean(
          connection?.pageInfo.hasNextPage && connection.pageInfo.endCursor,
        );
        after = connection?.pageInfo.endCursor ?? undefined;
      }

      return allEntities;
    };

    loadAllPages()
      .then((allEntities) => {
        if (!cancelled) setLoaded({ arenaId, entities: allEntities });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ arenaId, entities: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [arenaId, client]);

  const isLoadedForArena = loaded.arenaId === arenaId;

  return {
    entities: isLoadedForArena ? loaded.entities : [],
    loading: Boolean(arenaId) && !isLoadedForArena,
  };
};
